import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { TESTIMONIALS } from "@/lib/data";

function useVisibleCount() {
  const getCount = () => {
    if (typeof window === "undefined") return 4;
    if (window.innerWidth < 640) return 1;
    if (window.innerWidth < 1024) return 2;
    return 4;
  };
  const [count, setCount] = useState(getCount);
  useEffect(() => {
    const handler = () => setCount(getCount());
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return count;
}

const variants = {
  enter: (dir) => ({ opacity: 0, x: dir > 0 ? 60 : -60 }),
  center: { opacity: 1, x: 0 },
  exit: (dir) => ({ opacity: 0, x: dir > 0 ? -60 : 60 }),
};

export default function Testimonials() {
  const visibleCount = useVisibleCount();
  const pageCount = Math.ceil(TESTIMONIALS.length / visibleCount);
  const [page, setPage] = useState(0);
  const [direction, setDirection] = useState(1);
  const timerRef = useRef(null);

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setDirection(1);
      setPage((p) => (p + 1) % pageCount);
    }, 6000);
  }, [pageCount]);

  useEffect(() => {
    setPage(0);
  }, [visibleCount]);

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [startTimer]);

  const navigate = (dir) => {
    setDirection(dir);
    setPage((p) => ((p + dir) % pageCount + pageCount) % pageCount);
    startTimer();
  };

  const goTo = (i) => {
    setDirection(i > page ? 1 : -1);
    setPage(i);
    startTimer();
  };

  const slice = TESTIMONIALS.slice(page * visibleCount, page * visibleCount + visibleCount);

  return (
    <section
      id="depoimentos"
      data-testid="testimonials-section"
      className="bg-[#0A0A0A] py-24 sm:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="max-w-3xl mb-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-xs tracking-overline uppercase text-[#D4AF37] mb-6"
          >
            Vidas Transformadas
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            data-testid="testimonials-title"
            className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight"
          >
            Histórias da nossa{" "}
            <span className="italic text-white/80">comunidade</span>{" "}
            <span className="text-[#D4AF37]">brasileira</span>.
          </motion.h2>
        </div>

        <div className="overflow-hidden">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={page}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.5, ease: "easeInOut" }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
            >
              {slice.map((t) => (
                <blockquote
                  key={t.name}
                  className="relative bg-[#050505] p-5 sm:p-6 border border-white/5 hover:border-[#D4AF37]/30 transition-colors duration-500 flex flex-col"
                >
                  <Quote size={18} strokeWidth={1} className="text-[#D4AF37] mb-3 shrink-0" />
                  <p className="font-serif-display text-sm sm:text-base text-white/90 leading-snug italic flex-1">
                    "{t.quote}"
                  </p>
                  <footer className="mt-4 pt-4 border-t border-white/10">
                    <p className="text-white text-sm">{t.name}</p>
                    <p className="text-[10px] tracking-overline uppercase text-[#D4AF37] mt-0.5">
                      {t.role}
                    </p>
                  </footer>
                </blockquote>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex items-center justify-center gap-4 mt-10">
          <button
            onClick={() => navigate(-1)}
            aria-label="Anterior"
            className="w-10 h-10 flex items-center justify-center border border-white/10 hover:border-[#D4AF37]/50 text-white/50 hover:text-[#D4AF37] transition-colors duration-300"
          >
            <ChevronLeft size={20} />
          </button>

          <div className="flex items-center gap-2">
            {Array.from({ length: pageCount }).map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Página ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === page
                    ? "bg-[#D4AF37] w-6"
                    : "bg-white/20 w-2 hover:bg-white/40"
                }`}
              />
            ))}
          </div>

          <button
            onClick={() => navigate(1)}
            aria-label="Próximo"
            className="w-10 h-10 flex items-center justify-center border border-white/10 hover:border-[#D4AF37]/50 text-white/50 hover:text-[#D4AF37] transition-colors duration-300"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    </section>
  );
}
